import * as React from 'react';

import {EmptyStateSmall} from '../empty-states/empty-state-small';
import {Illustration} from '../illustrations/illustration';
import {Link} from '../link/link';
import {LoadingPage} from '../loading-page/loading-page';
import {Table} from '../table/table';

import styles from './form-locations-table.module.css';

type FormLocation = {
    url: string;
    lastSubmissionDate: any | null | undefined;
    submissionsCount: number;
    conversionRate: number;
};

type Props = {
    linkHeaderTitle: string;
    includeStats: boolean;
    data: FormLocation[];
    isLoading: boolean;
};

export function FormLocationsTable(props: Props) {
    const {isLoading, data, includeStats} = props;
    const linkColumns = [
        {
            Header: props.linkHeaderTitle,
            id: 'link',
            accessor: (formLocation: FormLocation) => formLocation,
            dataType: 'component',
            minWidth: 175,
            width: 175,
            maxWidth: 225,
            disableSortBy: false,
            hasRightBorder: true,
            hasLeftBorder: true,
            shouldTruncate: true,
            Cell: ({cell}: any) => {
                return (
                    <Link href={cell.value.url} variant='primary' size='small' newTab={true}>
                        {cell.value.url}
                    </Link>
                );
            },
        },
    ];

    const statsColumns = [
        {
            Header: 'Last submission',
            id: 'lastSubmissionDate',
            accessor: (formLocation: FormLocation) =>
                formLocation.lastSubmissionDate ? formLocation.lastSubmissionDate : undefined,
            dataType: 'timestamp',
            minWidth: 50,
            width: 75,
            maxWidth: 100,
        },
        {
            Header: 'Total submissions',
            id: 'submissionsCount',
            accessor: (formLocation: FormLocation) => formLocation.submissionsCount,
            dataType: 'string',
            minWidth: 55,
            width: 55,
            maxWidth: 75,
        },
        {
            Header: 'Conversion rate',
            id: 'conversionRate',
            accessor: (formLocation: FormLocation) => formLocation.conversionRate,
            dataType: 'percentage',
            minWidth: 55,
            width: 55,
            maxWidth: 75,
            hasRightBorder: true,
        },
    ];

    const columns = React.useMemo(
        // @ts-expect-error
        () => (includeStats ? linkColumns.concat(statsColumns) : linkColumns),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <div className={styles['form-locations-table-container']}>
            <Table
                isResizable={true}
                hasHeaderBorder={true}
                hasHeaderTopBorder={true}
                hasHeaderLeftBorder={true}
                hasHeaderRightBorder={true}
                hasHeaderCellBorders={true}
                hasBorders={true}
                isCompact={true}
                disableFilters={true}
                // @ts-expect-error
                columns={columns}
                data={data}
                sortBy={[
                    {
                        id: 'lastSubmissionDate',
                        desc: true,
                    },
                ]}
                specialStates={[
                    {
                        shouldRender: () => isLoading,
                        component: <LoadingPage padding={16} />,
                    },
                    {
                        // Empty state
                        shouldRender: () => !isLoading && !data.length,
                        component: (
                            <EmptyStateSmall
                                title='You haven’t embedded this form on any pages yet.'
                                subtitle='Website URLs will appear here once you do'
                                icon={
                                    <div style={{width: 80, height: 80}}>
                                        <Illustration
                                            src='cactus-empty-state'
                                            width='100%'
                                            height='100%'
                                        />
                                    </div>
                                }
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
}
